/**
 * Entity options — the slim `{ id, …labelColumns }` read behind every
 * entity picker (`useEntityOptions` in apps/web), served by
 *
 *   GET /api/v1/entity-options/orgs/:orgId/:table?<filter>=<value>  → { items }
 *
 * `ENTITY_OPTION_TABLES` is the one registry of readable tables: the route
 * resolves `:table` against it (unknown → 404, before any query), accepts
 * only that table's `filters` as query params (anything else, or a value the
 * filter's zod type rejects → 400), and selects exactly `id` plus
 * `labelColumns` — never `*`.
 *
 * Whether a table also returns the global rows (`organization_id IS NULL`) is
 * decided by `isDualScopeTableName` from `./dualScopeTypes` — there is no
 * second scope flag here. RLS on the caller's client is the permission
 * boundary (funds / budget groups come back empty without the accounting
 * scope). No `is_active` filter: a stored inactive id keeps its label.
 */
import { z } from "zod";
/** A query-string boolean: exactly `'true'` / `'false'`. */
const queryBoolean = z.enum(["true", "false"]).transform((v) => v === "true");
export const ENTITY_OPTION_TABLES = {
    projects: { labelColumns: ["name"] },
    funds: { labelColumns: ["fund_name"] },
    businesses: { labelColumns: ["name"] },
    people: { labelColumns: ["first_name", "last_name"] },
    asset_types: { labelColumns: ["name"] },
    procurement_types: { labelColumns: ["name"] },
    recycled_content_types: { labelColumns: ["name"] },
    end_of_life_plans: { labelColumns: ["name"] },
    waste_treatment_methods: { labelColumns: ["name"] },
    budget_category_groups: {
        labelColumns: ["name"],
        filters: { budget_id: z.string().uuid(), is_income: queryBoolean },
    },
};
export function isEntityOptionTableName(value) {
    return Object.prototype.hasOwnProperty.call(ENTITY_OPTION_TABLES, value);
}
/** The filter param names a table accepts (empty when it has none). */
export function entityOptionFilterNames(table) {
    const spec = ENTITY_OPTION_TABLES[table];
    return Object.keys(spec.filters ?? {});
}
/** One option: `id` plus the table's label columns (each a string or null). */
export const entityOptionItemSchema = z
    .object({ id: z.string() })
    .catchall(z.string().nullable());
export const entityOptionsResponseSchema = z.object({
    items: z.array(entityOptionItemSchema),
});
//# sourceMappingURL=entityOptions.js.map